import { firebaseApp, firestore } from 'app';
import { getFirestore, collection, doc, addDoc, updateDoc, onSnapshot, query, where, orderBy, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { VideoCall, CallSignal, CallCreateData, CallParticipant, CallPermissions, CallStatus } from './videoCallTypes';
import brain from 'brain';

const db = firestore;

// Permission system based on user roles
export const getCallPermissions = (userRole: string): CallPermissions => {
  switch (userRole) {
    case 'admin':
    case 'staff':
      return {
        canInitiateCalls: true,
        canReceiveCalls: true,
        allowedTargetRoles: ['admin', 'staff', 'client', 'limited_access']
      };
    case 'client':
    case 'limited_access':
      return {
        canInitiateCalls: true,
        canReceiveCalls: true,
        allowedTargetRoles: ['admin'] // Can only call admin users
      };
    default:
      return {
        canInitiateCalls: false,
        canReceiveCalls: false,
        allowedTargetRoles: []
      };
  }
};

// Get admin users for client calls (similar to chat system)
export const getAdminUsersForCall = async (): Promise<CallParticipant[]> => {
  try {
    const usersRef = collection(db, 'users');
    const adminQuery = query(usersRef, where('role', '==', 'admin'), where('isActive', '==', true));
    
    return new Promise((resolve, reject) => {
      const unsubscribe = onSnapshot(adminQuery, (snapshot) => {
        const adminUsers: CallParticipant[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Respect hideFromClientView (test/admin accounts excluded from calls)
          if (data.hideFromClientView === true) return;
          adminUsers.push({
            id: doc.id,
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || 'Admin User',
            role: data.role,
            photoURL: data.photoURL,
            isVideoEnabled: true,
            isAudioEnabled: true
          });
        });
        unsubscribe();
        resolve(adminUsers);
      }, reject);
    });
  } catch (error) {
    console.error('Error fetching admin users for call:', error);
    return [];
  }
};

// Create a new video call
export const initiateCall = async (callData: CallCreateData, currentUser: CallParticipant): Promise<string> => {
  try {
    // Check permissions
    const permissions = getCallPermissions(currentUser.role);
    if (!permissions.canInitiateCalls) {
      throw new Error('You do not have permission to initiate calls');
    }

    // Use backend API to create call (bypasses Firestore security rules)
    const response = await brain.initiate_call({
      type: callData.type,
      participants: callData.participants,
      conversationId: callData.conversationId || null
    });

    const result = await response.json();
    return result.callId;
  } catch (error) {
    console.error('Error initiating call:', error);
    throw error;
  }
};

// Update call status
export const updateCallStatus = async (callId: string, status: CallStatus, additionalData?: any): Promise<void> => {
  try {
    // Use backend API to update call status (bypasses Firestore security rules)
    await brain.update_call_status(
      { callId },
      { status: status as any, additionalData: additionalData || null }
    );
  } catch (error) {
    console.error('Error updating call status:', error);
    throw error;
  }
};

// Subscribe to incoming calls for a user
export const subscribeToIncomingCalls = (
  userId: string,
  callback: (calls: VideoCall[]) => void
) => {
  console.log('[VideoCall] Setting up incoming calls listener for user:', userId);
  
  const q = query(
    collection(db, 'video_calls'),
    where('participants', 'array-contains', userId),
    where('status', 'in', ['initiating', 'ringing']),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    console.log('[VideoCall] Listener triggered, snapshot size:', snapshot.size);
    
    const calls: VideoCall[] = [];
    snapshot.forEach((docSnapshot) => {
      console.log('[VideoCall] Found call document:', docSnapshot.id, docSnapshot.data());
      const data = docSnapshot.data();
      calls.push({
        id: docSnapshot.id,
        type: data.type,
        callerId: data.callerId,
        callerName: data.callerName,
        callerRole: data.callerRole,
        participants: data.participants,
        participantDetails: data.participantDetails,
        status: data.status,
        startedAt: data.startedAt?.toDate(),
        endedAt: data.endedAt?.toDate(),
        duration: data.duration,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      });
    });
    callback(calls);
  });
};

// Subscribe to active call updates
export const subscribeToCall = (
  callId: string,
  callback: (call: VideoCall | null) => void
) => {
  return onSnapshot(doc(db, 'video_calls', callId), (docSnapshot) => {
    if (!docSnapshot.exists()) {
      callback(null);
      return;
    }

    const data = docSnapshot.data();
    const call: VideoCall = {
      id: docSnapshot.id,
      type: data.type,
      callerId: data.callerId,
      callerName: data.callerName,
      callerRole: data.callerRole,
      participants: data.participants,
      participantDetails: data.participantDetails,
      status: data.status,
      startedAt: data.startedAt?.toDate(),
      endedAt: data.endedAt?.toDate(),
      duration: data.duration,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
    callback(call);
  });
};

// Send WebRTC signaling data
export const sendSignal = async (signal: Omit<CallSignal, 'id' | 'timestamp'>): Promise<void> => {
  try {
    // Use backend API instead of Firestore to bypass permissions
    await brain.send_signal(
      { callId: signal.callId },
      {
        fromUserId: signal.fromUserId,
        toUserId: signal.toUserId,
        type: signal.type as 'offer' | 'answer' | 'ice-candidate' | 'call-accept' | 'call-decline' | 'call-end',
        data: signal.data
      }
    );
  } catch (error) {
    console.error('Error sending signal:', error);
    throw error;
  }
};

// Subscribe to WebRTC signals for a call using polling
export const subscribeToSignals = (
  callId: string,
  userId: string,
  callback: (signals: CallSignal[]) => void
) => {
  let isActive = true;
  const processedSignalIds = new Set<string>();
  
  const pollSignals = async () => {
    if (!isActive) return;
    
    try {
      // Fetch signals from backend API
      const response = await brain.get_signals({ callId });
      
      // Check response status
      if (!response.ok) {
        console.error('[Signal Polling] Non-OK response:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[Signal Polling] Error body:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const signalsData = await response.json();
      
      // Filter out already processed signals
      const newSignals: CallSignal[] = [];
      for (const signal of signalsData) {
        if (!processedSignalIds.has(signal.id)) {
          processedSignalIds.add(signal.id);
          newSignals.push({
            id: signal.id,
            callId: signal.callId,
            fromUserId: signal.fromUserId,
            toUserId: signal.toUserId,
            type: signal.type as any,
            data: signal.data,
            timestamp: (signal as any).timestamp || Date.now(),
          });
        }
      }
      
      if (newSignals.length > 0) {
        callback(newSignals);
      }
    } catch (error: any) {
      // 404 means no signals exist yet or call not found - this is normal, just continue polling
      const is404 = error?.response?.status === 404 || error?.status === 404;
      
      if (!is404) {
        // Log detailed error information
        console.error('[Signal Polling] Error:', error);
        console.error('[Signal Polling] Error status:', error?.status);
        console.error('[Signal Polling] Error response:', error?.response);
        console.error('[Signal Polling] Full error object:', JSON.stringify(error, null, 2));
      }
    }
    
    // Poll again after 500ms
    if (isActive) {
      setTimeout(pollSignals, 500);
    }
  };
  
  // Start polling
  pollSignals();
  
  // Return unsubscribe function
  return () => {
    isActive = false;
  };
};

// Clean up processed signals
export const deleteSignal = async (signalId: string): Promise<void> => {
  try {
    // Use backend API instead of Firestore to bypass permissions
    await brain.delete_signal({ signalId });
  } catch (error) {
    console.error('Error deleting signal:', error);
  }
};

// Accept an incoming call
export const acceptCall = async (callId: string, userId: string): Promise<void> => {
  try {
    // Use the new accept endpoint which implements first-to-answer logic
    const response = await brain.accept_call(
      { callId },
      { userId }
    );
    const result = await response.json();
    console.log('[VideoCall] Call accepted, removed participants:', result.removedParticipants);
  } catch (error) {
    console.error('Error accepting call:', error);
    throw error;
  }
};

// Decline an incoming call
export const declineCall = async (callId: string, userId: string): Promise<void> => {
  try {
    await updateCallStatus(callId, 'declined');
    await sendSignal({
      callId,
      fromUserId: userId,
      toUserId: '', // Will be handled by all participants
      type: 'call-decline',
      data: { declinedBy: userId }
    });
  } catch (error) {
    console.error('Error declining call:', error);
    throw error;
  }
};

// End an active call
export const endCall = async (callId: string, userId: string): Promise<void> => {
  try {
    await updateCallStatus(callId, 'ended', { endedBy: userId });
    await sendSignal({
      callId,
      fromUserId: userId,
      toUserId: '', // Will be handled by all participants
      type: 'call-end',
      data: { endedBy: userId }
    });
  } catch (error) {
    console.error('Error ending call:', error);
    throw error;
  }
};

// Create a call message in chat
export const createCallMessage = async (
  conversationId: string,
  callerId: string,
  callerName: string,
  duration: number,
  status: 'completed' | 'missed' | 'declined'
): Promise<void> => {
  try {
    // Use backend API to create call message (bypasses Firestore security rules)
    await brain.create_call_message({
      conversationId,
      callerId,
      callerName,
      duration,
      status
    } as any);
    
    console.log(`[VideoCall] Created call message in conversation ${conversationId}`);
  } catch (error) {
    console.error('Error creating call message:', error);
    // Don't throw - call message is not critical
  }
};
